import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { fetchTrackingData, type TrackingData, type TrackingMilestone } from "../../api/dashboard";

type MilestoneStatus = "Completed" | "Active" | "Upcoming";

const statusStyles: Record<MilestoneStatus, string> = {
  Completed: "bg-green-50 text-green-600 border-green-100",
  Active: "bg-blue-50 text-blue-600 border-blue-100",
  Upcoming: "bg-gray-100 text-gray-500 border-gray-200",
};

export default function TrackingScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const hackathonId = (location.state as { hackathonId?: string } | null)?.hackathonId;

  const [data, setData] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !hackathonId) { setLoading(false); return; }
    fetchTrackingData(token, hackathonId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token, hackathonId]);

  const milestones: TrackingMilestone[] = data?.milestones ?? [];
  const completedCount = data?.completedCount ?? 0;
  const totalCount = data?.totalCount ?? 0;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 flex justify-center py-10 px-4">
        <div className="w-full max-w-3xl space-y-6">
          {/* Back Navigation */}
          <button
            onClick={() => navigate("/user-dashboard")}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>

          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Timeline & Deadlines</h1>
              <p className="text-sm text-gray-500">Track your hackathon milestones and key dates</p>
            </div>
            {data && (
              <span className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white text-gray-700">
                {data.hackathonTitle}
              </span>
            )}
          </div>

          {loading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse space-y-3">
              <div className="h-4 bg-gray-100 rounded w-40" />
              <div className="h-2 bg-gray-100 rounded w-full" />
            </div>
          ) : !data ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <p className="text-gray-400">No tracking data available.</p>
            </div>
          ) : (
            <>
              {/* Overall Progress */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-gray-700">Overall Progress</h2>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{completedCount} of {totalCount} milestones completed</span>
                  <span>{progressPct}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${progressPct}%` }} />
                </div>
              </div>

              {/* Milestones */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <h2 className="text-sm font-medium text-gray-700">Milestones</h2>
                </div>

                <ul className="space-y-6">
                  {milestones.map((m, index) => (
                    <li key={index} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        {m.status === "Completed" ? (
                          <div className="w-8 h-8 rounded-full bg-green-500 border-2 border-green-500 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : m.status === "Active" ? (
                          <div className="relative flex items-center justify-center flex-shrink-0">
                            <div className="absolute w-12 h-12 rounded-full bg-blue-100 animate-ping opacity-50" />
                            <div className="absolute w-10 h-10 rounded-full bg-blue-100" />
                            <div className="w-8 h-8 rounded-full bg-blue-500 border-2 border-blue-500 flex items-center justify-center z-10">
                              <div className="w-3 h-3 rounded-full bg-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center flex-shrink-0" />
                        )}
                        {index !== milestones.length - 1 && (
                          <div className="w-px flex-1 bg-gray-200 mt-1" />
                        )}
                      </div>

                      <div className="flex-1 flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">{m.title}</h3>
                          <p className="text-xs text-gray-500 mt-0.5">{m.date}</p>
                          <p className="text-xs text-gray-400 mt-1">{m.description}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyles[m.status]}`}>
                          {m.status}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
